/**
 * The kernel session claims of the in-memory access token, read for the UI (kernel apps, WS-17).
 *
 * A kernel session token (WS-11) names the kernel origin in its `aud` list, and carries `amr`,
 * `auth_time` and, after a step-up of the same session family, `stepUpAt` (WS-1b `jwt-payload.ts`,
 * R-067). A UI on the kernel origin reads them to show whether the tier-2 window of decision 4 is
 * open, and when it closes.
 *
 * Every function here decodes and never verifies a signature. Its answer is a hint for the UI
 * only: core-api decides every operation. It imports no `react`, so `@zion/sdk-react/auth-core`
 * re-exports it. No function throws.
 */
import { authenticationMethodListSchema, authTimeSchema, isWithinStepUpWindow, STEP_UP_WINDOW_SECONDS, } from '@zion/sdk-contracts';
import { parseJwt } from '../token-utils.js';
import { isKernelOrigin } from './kernel-refusal.js';
/** The claims of no kernel session: `false`, an empty list, `null` and `null`. A frozen object. */
export const NO_KERNEL_SESSION = Object.freeze({
    kernelSession: false,
    amr: Object.freeze([]),
    authTime: null,
    stepUpAt: null,
});
/** The value when `authTimeSchema` accepts it (a safe integer of 0 or more), else `null`. */
function epochSecondOf(value) {
    const parsed = authTimeSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/** True when `aud` is a list of two strings or more and a member after the first equals `origin`. */
function audienceNamesOrigin(aud, origin) {
    if (!Array.isArray(aud) || aud.length < 2)
        return false;
    if (!aud.every((member) => typeof member === 'string'))
        return false;
    return aud.slice(1).includes(origin);
}
/**
 * The kernel session claims of `token` on `origin`.
 *
 * It answers {@link NO_KERNEL_SESSION} in four cases: a `null` token, a token that `parseJwt`
 * refuses, a payload that is not a JSON object, and an `origin` that fails `isKernelOrigin`.
 * Otherwise:
 *
 * - `kernelSession` is `true` when `aud` is an array of two strings or more and a member after the
 *   first equals `origin` (`research/p3-sessions-across-origins.md`, section 6);
 * - `amr` is the value that `authenticationMethodListSchema` accepts, else an empty list;
 * - `authTime` is `auth_time` when `authTimeSchema` accepts it, else `null`;
 * - `stepUpAt` follows the `auth_time` rule, and is `null` when `kernelSession` is `false`.
 *
 * Pure; never throws.
 */
export function kernelSessionClaimsOf(token, origin) {
    if (token === null || !isKernelOrigin(origin))
        return NO_KERNEL_SESSION;
    let payload;
    try {
        payload = parseJwt(token);
    }
    catch {
        return NO_KERNEL_SESSION;
    }
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload))
        return NO_KERNEL_SESSION;
    const claims = payload;
    const kernelSession = audienceNamesOrigin(claims['aud'], origin);
    const amr = authenticationMethodListSchema.safeParse(claims['amr']);
    return {
        kernelSession,
        amr: amr.success ? amr.data : [],
        authTime: epochSecondOf(claims['auth_time']),
        stepUpAt: kernelSession ? epochSecondOf(claims['stepUpAt']) : null,
    };
}
/**
 * True when the tier-2 window is open at `nowSeconds`: a kernel session whose `stepUpAt` lies
 * within `isWithinStepUpWindow` (0 to 300 seconds ago, both ends included). `false` for no kernel
 * session, for no `stepUpAt` and for a `stepUpAt` in the future. Pure.
 */
export function isStepUpWindowOpen(claims, nowSeconds) {
    if (!claims.kernelSession)
        return false;
    return isWithinStepUpWindow(claims.stepUpAt ?? undefined, nowSeconds);
}
/**
 * The epoch second at which the tier-2 window closes: `stepUpAt + STEP_UP_WINDOW_SECONDS` for a
 * kernel session with a `stepUpAt`, else `null`. The window is still open at that second and
 * closed one second later. Pure.
 */
export function stepUpWindowClosesAt(claims) {
    if (!claims.kernelSession || claims.stepUpAt === null)
        return null;
    return claims.stepUpAt + STEP_UP_WINDOW_SECONDS;
}
//# sourceMappingURL=kernel-session.js.map